<template>
    <div class="popup-backdrop">
        <div class="popup-modal">
            <div class="popup-header">
                <span class="popup-title">환자 불러오기</span>
            </div>
            <template v-if="mode == 'error'">
                <div class="popup-text">엑셀에서 아래의 행에 오류가 발생했습니다.</div>
                <div class="popup-text mt10">오류가 발생한 데이터는 공란으로 변경되어 저장됩니다.</div>
                <br>
                <div class="popup-text mb10" style="justify-content: normal; overflow-y: auto;">
                    <span class="diag-title" v-for="err in errorList" :key="err">{{err}}</span>
                </div>
                
                <div class="popup-btn-group">
                    <button class="gray-btn" @click="closePopup">취소</button>
                    <button class="blue-btn" @click="mode = 'select'">진행</button>
                </div>
            </template>
            <template v-if="mode == 'select'">
                <table class="popup-patient-table">
                    <thead>
                        <tr>
                            <th>환자번호</th>
                            <th>환자명</th>
                            <th>생년월일</th>
                            <th>몸무게</th>
                            <th>전화번호</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in rowList"
                            :key="row.patientId"
                            @click="toggleRow(row.patientId)"
                            :class="{ 'row-selected': selectedRows.includes(row.patientId) }"
                        >
                            <td>{{ row.patientId }}</td>
                            <td>{{ row.name }}</td>
                            <td>{{ row.dob }}</td>
                            <td>{{ convertUtils.convertWeight(row.weight) }}</td>
                            <td>{{ formatPhone(row.contactNumber) }}</td>
                        </tr>
                    </tbody>
                </table>
                <div class="popup-btn-group">
                    <button class="popup-cancel-btn" @click="closePopup">취소</button>
                    <button class="popup-save-btn" @click="saveAll">전체 등록</button>
                </div>
                <pagination
                    :total-items="totalCount"
                    :page-size="pageSize"
                    v-model="currentPage"
                    @page-change="onPageChange"
                />
            </template>
        </div>
    </div>
</template>
<script setup>
    import { onMounted, ref, watch } from 'vue'
    import api, {urls}               from "@/api";
    import Pagination                from "@/components/common/Pagination.vue";
    import { useModal }              from '@/composables/useModal'
    import { useAppAlert }           from '@/composables/useAppAlert'

    const { saveAndClose } = useModal()
    const { showAlert, showAlertAndMove, confirm } = useAppAlert()
    
    import { useAuthStore } from '@/stores/auth';
    import { convertUtils } from '@/utils/convertUtils'

    const emit = defineEmits(['close'])

    const mode            = ref('select')
    const headerList      = ref([])
    const errorList       = ref([])
    const rowList         = ref([])
    const currentPage     = ref(1)
    const pageSize        = ref(10)
    const totalPages      = ref(1)
    const totalCount      = ref(0)
    const selectedRows    = ref([])
    const selectAll       = ref(false)
    const selectedHeaders = ref([])
    const writeContent    = ref([])
    const contentAll      = ref(false)

    // 페이지 변경 이벤트 핸들러
    function onPageChange(page) {
        fetchPatients(page)
    }

    function fetchPatients(page = 1){
        api.get(urls.patientAdd.getList, {
            params: {
                startIndex: page,
                pageRow: pageSize.value
            }
        })
        .then(res => {
            rowList.value    = res.data.list || res.data || [];
            headerList.value = Object.keys(res.data.header) || []
            errorList.value  = res.data.errorList || []
            
            if(errorList.value.length > 0){
                mode.value = 'error'
            }
            
            totalCount.value  = res.data.totCnt;
            totalPages.value  = Math.ceil(totalCount.value / pageSize.value);
            currentPage.value = page;

            if (res.data.msg)  {
                showAlert(res.data.msg)
            }
        })
        .catch(err =>{
            showAlert('환자 목록을 불러오는데 실패했습니다.')
            rowList.value     = []
            headerList.value  = []
            totalCount.value  = 0
            totalPages.value  = 1
            currentPage.value = 1
        })
    }

    function toggleSelectAll() {
        if (selectAll.value) {
            selectedRows.value = rowList.value.map(p => p.patientId)
        } else {
            selectedRows.value = []
        }
    }
    function toggleRow(id) {
        if (selectedRows.value.includes(id)) {
            selectedRows.value = selectedRows.value.filter(i => i != id) // 이미 있으면 제거
        } else {
            selectedRows.value.push(id)       // 없으면 추가
        }
    }

    function toggleContentAll() {
        if (contentAll.value) {
            selectedHeaders.value = headerList.value
        } else {
            selectedHeaders.value = []
        }
    }
    function toggleHeader(id) {
        if (selectedHeaders.value.includes(id)) {
            selectedHeaders.value = selectedHeaders.value.filter(i => i != id) // 이미 있으면 제거
        } else {
            selectedHeaders.value.push(id)       // 없으면 추가
        }
    }

    watch(selectedRows, () => {
        selectAll.value =
            selectedRows.value.length > 0 &&
            selectedRows.value.length === rowList.value.length
    })

    function saveAll() {
        confirm('정말로 저장하시겠습니까?').then((ok) => {
            if (!ok) return

            const authStore = useAuthStore()
            const inst_cd   = authStore.getUser?.inst_cd

            api.get(urls.patientAdd.saveAll, { params: { instCd: inst_cd } })
            .then(res => {
                const code = res.data.code
                const msg = res.data.msg

                showAlert(msg)
                if (code === 'success') {
                saveAndClose('loadPatientModal', true)
                }
            })
            .catch(() => {
                showAlert('외부 환자들 저장 중 오류가 발생했습니다.')
            })
        })
    }

    function closePopup() {
        emit('close')
    }

    function formatPhone(phone) {
        if (!phone) return '';
        const onlyNums = phone.replace(/[^0-9]/g, '');
        if (onlyNums.length === 11) {
            return onlyNums.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
        } else if (onlyNums.length === 10) {
            return onlyNums.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
        }
        return phone; // 나머지는 원본 유지
    }

    onMounted(() => {
        fetchPatients()
    })
</script>